import React, { useState, useEffect } from "react";
import { createHomonym, updateHomonym, fetchHomonymById } from "./adminHomonymService";
import "../../styles/content.css";

const defaultMeaning = { meaning: "", context: "", exampleSentence: "", sortOrder: "" };

const defaultForm = {
  word: "", ipaPronunciation: "", phoneticPronunciation: "",
  sortOrder: "", youtubeLink: "", meanings: [{ ...defaultMeaning }],
};

const HomonymForm = ({ homonym, onSaved, onCancel }) => {
  const isEdit = !!homonym;
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (homonym?.id) {
      fetchHomonymById(homonym.id).then((res) => {
        if (res.status === 200 && res.data) {
          const d = res.data;
          setForm({
            word: d.word || "",
            ipaPronunciation: d.ipaPronunciation || "",
            phoneticPronunciation: d.phoneticPronunciation || "",
            sortOrder: d.sortOrder ?? "",
            youtubeLink: d.youtubeLink || "",
            meanings: d.meanings?.length
              ? d.meanings.map((m) => ({
                  meaning: m.meaning || "", context: m.context || "",
                  exampleSentence: m.exampleSentence || "", sortOrder: m.sortOrder ?? "",
                }))
              : [{ ...defaultMeaning }],
          });
        }
      });
    }
  }, [homonym]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleMeaningChange = (idx, e) => {
    const updated = [...form.meanings];
    updated[idx] = { ...updated[idx], [e.target.name]: e.target.value };
    setForm({ ...form, meanings: updated });
  };

  const addMeaning = () => setForm({ ...form, meanings: [...form.meanings, { ...defaultMeaning }] });

  const removeMeaning = (idx) => {
    if (form.meanings.length <= 1) return;
    setForm({ ...form, meanings: form.meanings.filter((_, i) => i !== idx) });
  };

  const handleSubmit = async () => {
    if (!form.word) { alert("Word is required"); return; }
    if (!form.meanings.some((m) => m.meaning.trim())) { alert("At least one meaning is required"); return; }

    const payload = {
      ...form,
      sortOrder: form.sortOrder !== "" ? Number(form.sortOrder) : null,
      youtubeLink: form.youtubeLink || null,
      meanings: form.meanings
        .filter((m) => m.meaning.trim())
        .map((m, i) => ({
          ...m,
          sortOrder: m.sortOrder !== "" ? Number(m.sortOrder) : i + 1,
        })),
    };

    setLoading(true);
    const res = isEdit ? await updateHomonym(homonym.id, payload) : await createHomonym(payload);
    if (res.status === 200 || res.status === 201) {
      onSaved();
    } else {
      alert(res.message || "Operation failed");
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: 650, maxHeight: "90vh", overflowY: "auto" }}>
        <div className="form-card" style={{ margin: 0, boxShadow: "none" }}>
          <h2>{isEdit ? "Edit" : "Create"} Homonym</h2>

          <div className="form-group">
            <label>Word *</label>
            <input name="word" placeholder="e.g. Draw" value={form.word} onChange={handleChange} />
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>IPA Pronunciation</label>
              <input name="ipaPronunciation" placeholder="/drɔː/" value={form.ipaPronunciation} onChange={handleChange} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Phonetic Pronunciation</label>
              <input name="phoneticPronunciation" placeholder="draw" value={form.phoneticPronunciation} onChange={handleChange} />
            </div>
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Sort Order</label>
              <input name="sortOrder" type="number" value={form.sortOrder} onChange={handleChange} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>YouTube Link</label>
              <input name="youtubeLink" placeholder="https://youtube.com/..." value={form.youtubeLink} onChange={handleChange} />
            </div>
          </div>

          <h3 style={{ marginTop: 16 }}>Meanings</h3>
          {form.meanings.map((m, idx) => (
            <div key={idx} style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <strong>Meaning #{idx + 1}</strong>
                {form.meanings.length > 1 && (
                  <button type="button" onClick={() => removeMeaning(idx)} style={{ color: "red", background: "none", border: "none", cursor: "pointer" }}>✕ Remove</button>
                )}
              </div>
              <div className="form-group">
                <label>Meaning *</label>
                <input name="meaning" placeholder="e.g. To pull something" value={m.meaning} onChange={(e) => handleMeaningChange(idx, e)} />
              </div>
              <div className="form-group">
                <label>Context</label>
                <input name="context" placeholder="e.g. Physical action" value={m.context} onChange={(e) => handleMeaningChange(idx, e)} />
              </div>
              <div className="form-group">
                <label>Example Sentence</label>
                <input name="exampleSentence" placeholder="e.g. Draw the curtains please." value={m.exampleSentence} onChange={(e) => handleMeaningChange(idx, e)} />
              </div>
              <div className="form-group">
                <label>Sort Order</label>
                <input name="sortOrder" type="number" value={m.sortOrder} onChange={(e) => handleMeaningChange(idx, e)} />
              </div>
            </div>
          ))}
          <button type="button" onClick={addMeaning} style={{ marginBottom: 16 }}>+ Add Meaning</button>

          <div className="form-actions">
            <button onClick={handleSubmit} disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Update" : "Create"}
            </button>
            <button onClick={onCancel} disabled={loading}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomonymForm;
